import { useAuth } from '@clerk/nextjs';
import axios from 'axios';
import type {
  BookingDetails,
  BookingResponse,
  BookingType,
  MeetingToken,
  UpdateBookingDto,
  UpdateBookingTimeDto,
} from '@/types/booking';
import { getApiBaseUrl } from '@/lib/apiUrl';

export const useExpertService = () => {
  const { getToken } = useAuth();

  const getBookings = async (expertExternalId: string): Promise<BookingResponse[]> => {
    const token = await getToken();
    const response = await axios.get<BookingResponse[]>(
      `${getApiBaseUrl()}/experts/${expertExternalId}/bookings`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return response.data;
  };

  const getExpertBookings = async (
    expertExternalId: string,
    limit?: number,
    past: boolean = false,
  ): Promise<BookingType[]> => {
    const token = await getToken();
    const response = await axios.get<BookingType[]>(
      `${getApiBaseUrl()}/experts/${expertExternalId}/bookings?limit=${limit}&past=${past}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return response.data;
  };

  const getBookingDetails = async (
    expertExternalId: string,
    bookingId: string,
  ): Promise<BookingDetails> => {
    const token = await getToken();
    const response = await axios.get<BookingDetails>(
      `${getApiBaseUrl()}/experts/${expertExternalId}/bookings/${bookingId}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return response.data;
  };

  const updateBooking = async (
    expertExternalId: string,
    bookingId: string,
    updateBooking: UpdateBookingDto,
  ): Promise<BookingResponse> => {
    const token = await getToken();
    const response = await axios.post<BookingResponse>(
      `${getApiBaseUrl()}/experts/${expertExternalId}/bookings/${bookingId}`,
      updateBooking,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return response.data;
  };

  const getMeetingToken = async (
    bookingId: string,
    expertExternalId: string,
    isExpert: boolean,
  ): Promise<MeetingToken> => {
    const token = await getToken();
    const response = await axios.get<MeetingToken>(
      `${getApiBaseUrl()}/experts/${expertExternalId}/bookings/${bookingId}/meeting/token?isExpert=${isExpert}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return response.data;
  };

  const approveBooking = async (
    expertExternalId: string,
    bookingId: string,
    locale?: string,
  ): Promise<BookingResponse> => {
    const token = await getToken();
    const response = await axios.put<BookingResponse>(
      `${getApiBaseUrl()}/experts/${expertExternalId}/bookings/${bookingId}/approve`,
      { locale },
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return response.data;
  };

  const cancelBooking = async (
    expertExternalId: string,
    bookingId: string,
  ): Promise<{ success: boolean }> => {
    const token = await getToken();
    const res = await axios.put<void>(
      `${getApiBaseUrl()}/experts/${expertExternalId}/bookings/${bookingId}/cancel`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return { success: res.status === 200 };
  };

  const updateBookingTime = async (
    expertExternalId: string,
    bookingId: string,
    updateBookingTimeDto: UpdateBookingTimeDto,
  ): Promise<BookingDetails> => {
    const token = await getToken();
    const response = await axios.put<BookingDetails>(
      `${getApiBaseUrl()}/experts/${expertExternalId}/bookings/${bookingId}/update-time`,
      updateBookingTimeDto,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return response.data;
  };

  return {
    getBookings,
    getBookingDetails,
    updateBooking,
    getMeetingToken,
    approveBooking,
    cancelBooking,
    updateBookingTime,
    getExpertBookings,
  };
};
